import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { FileText, Download, ArrowLeft, Edit3, Save, X, FilePlus, CheckCircle, AlertTriangle, Unlock } from 'lucide-react';
import { incorporadoraDe } from '../../utils/incorporadora';

const STATUS_COLORS = {
  pendente:   'bg-yellow-100 text-yellow-800',
  em_analise: 'bg-blue-100 text-blue-800',
  aprovada:   'bg-green-100 text-green-800',
  recusada:   'bg-red-100 text-red-800',
};

function fmtBRL(val) {
  if (!val && val !== 0) return '—';
  return parseFloat(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function fmtData(str) {
  if (!str) return '—';
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
  }
  return str;
}

// ── Modal de confirmação de dados antes de gerar contrato ─────────
function GerarContratoModal({ proposta, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('confirm'); // confirm | success
  const [resultado, setResultado] = useState(null);

  const sim = proposta.simulacao_json || {};
  const incorp = incorporadoraDe(proposta.empreendimento);

  const handleGerar = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post(`/contratos/gerar-de-proposta/${proposta.id}`);
      setResultado(res.data.data);
      setStep('success');
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao gerar contrato. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {step === 'success' ? 'Contrato Gerado!' : 'Confirmar dados do Contrato'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {step === 'success'
                ? 'O contrato foi gerado e está aguardando envio para assinatura.'
                : `${proposta.empreendimento} — Unidade ${proposta.unidade_numero}`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
        </div>

        {step === 'success' ? (
          <div className="p-8 text-center">
            <CheckCircle size={56} className="mx-auto text-green-500 mb-4" />
            <p className="text-xl font-bold text-gray-800 mb-2">{resultado?.numero_contrato}</p>
            <p className="text-gray-500 text-sm mb-6">
              O contrato foi gerado com os dados da proposta.<br />
              Acesse a aba <strong>Contratos</strong> para visualizar, confirmar e enviar ao Clicksign.
            </p>
            <button onClick={onClose} className="btn-primary px-8">Ir para Contratos</button>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-5 max-h-[65vh] overflow-y-auto">

              {/* Unidade */}
              <section>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Unidade</h3>
                <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-3 gap-3 text-sm">
                  <div><span className="text-gray-400 block text-xs">Empreendimento</span><span className="font-medium">{proposta.empreendimento || '—'}</span></div>
                  <div><span className="text-gray-400 block text-xs">Unidade</span><span className="font-medium">{proposta.unidade_numero || '—'}</span></div>
                  <div><span className="text-gray-400 block text-xs">Andar</span><span className="font-medium">{proposta.andar ? `${proposta.andar}º` : '—'}</span></div>
                </div>
              </section>

              {/* Comprador */}
              <section>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Comprador</h3>
                <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-400 block text-xs">Nome</span><span className="font-medium">{proposta.cliente_nome || '—'}</span></div>
                  <div><span className="text-gray-400 block text-xs">CPF</span><span>{proposta.cliente_cpf || '—'}</span></div>
                  <div><span className="text-gray-400 block text-xs">Estado Civil</span><span>{proposta.cliente_estado_civil || '—'}</span></div>
                  <div><span className="text-gray-400 block text-xs">Profissão</span><span>{proposta.cliente_profissao || '—'}</span></div>
                  <div className="col-span-2"><span className="text-gray-400 block text-xs">Endereço</span><span>{proposta.cliente_endereco || '—'}</span></div>
                  <div><span className="text-gray-400 block text-xs">E-mail</span><span>{proposta.cliente_email || '—'}</span></div>
                  <div><span className="text-gray-400 block text-xs">WhatsApp</span><span>{proposta.cliente_whatsapp || '—'}</span></div>
                </div>
              </section>

              {/* Cônjuge */}
              {proposta.conjuge_nome && (
                <section>
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Cônjuge</h3>
                  <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-400 block text-xs">Nome</span><span className="font-medium">{proposta.conjuge_nome}</span></div>
                    <div><span className="text-gray-400 block text-xs">CPF</span><span>{proposta.conjuge_cpf || '—'}</span></div>
                    <div><span className="text-gray-400 block text-xs">Profissão</span><span>{proposta.conjuge_profissao || '—'}</span></div>
                  </div>
                </section>
              )}

              {/* Fluxo de pagamento */}
              {sim.ato && (
                <section>
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Fluxo de Pagamento</h3>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Ato ({fmtData(sim.ato?.data)})</span><span className="font-bold">{fmtBRL(sim.ato?.valor)}</span></div>
                    {sim.mensais?.quantidade > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">{sim.mensais.quantidade}x mensais — 1ª em {fmtData(sim.mensais.data_primeira)}</span>
                        <span className="font-medium">{fmtBRL(sim.mensais.valor_mensal)}/mês = {fmtBRL(sim.mensais.total)}</span>
                      </div>
                    )}
                    {sim.baloes?.quantidade > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">{sim.baloes.quantidade}x reforços ({sim.baloes.frequencia})</span>
                        <span className="font-medium">{fmtBRL(sim.baloes.valor_por_balao)} cada = {fmtBRL(sim.baloes.total)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between"><span className="text-gray-500">Total fluxo obra</span><span className="font-bold">{fmtBRL(sim.total_obra)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Saldo nas chaves</span><span className="font-bold text-primary">{fmtBRL(sim.financiamento_chaves)}</span></div>
                  </div>
                </section>
              )}

              {/* Vendedora */}
              <section>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Vendedora</h3>
                <div className="bg-gray-50 rounded-xl p-4 text-sm">
                  <p className="font-semibold">{incorp.razao_social}</p>
                  <p className="text-gray-500 text-xs">CNPJ: {incorp.cnpj}</p>
                  <p className="text-gray-500 mt-1">Representada por: <strong>Leonardo Fagner Botega</strong></p>
                  <p className="text-gray-500 text-xs">CPF: 048.762.131-38 · WhatsApp: (44) 99865-2663</p>
                </div>
              </section>

              {/* Testemunhas */}
              <section>
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Testemunhas</h3>
                <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs font-bold text-gray-400 mb-1">Testemunha 1 — Corretor</p>
                    <p className="font-medium">{proposta.corretor_nome || '—'}</p>
                    <p className="text-gray-500">{proposta.corretor_cpf || '—'} · CRECI {proposta.corretor_creci || '—'}</p>
                    <p className="text-gray-500">{proposta.corretor_email || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 mb-1">Testemunha 2 — Inbase</p>
                    <p className="font-medium">Fernando Estrada Perazzolo</p>
                    <p className="text-gray-500 text-xs">035.406.089-90 · WhatsApp: (44) 99137-1919</p>
                    <p className="text-gray-500">fernando@inbaseconstrutora.com</p>
                  </div>
                </div>
              </section>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  {error}
                </div>
              )}
            </div>

            <div className="flex gap-3 p-6 border-t justify-end">
              <button onClick={onClose} className="btn-secondary">Cancelar</button>
              <button
                onClick={handleGerar}
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                <FilePlus size={16} />
                {loading ? 'Gerando contrato...' : 'Confirmar e Gerar Contrato'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Detalhe da proposta ───────────────────────────────────────────
function PropostaDetail({ id, onBack }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showGerarModal, setShowGerarModal] = useState(false);
  const [unidadeLiberadaToast, setUnidadeLiberadaToast] = useState(false);

  const { data: proposta, isLoading } = useQuery({
    queryKey: ['proposta', id],
    queryFn: () => api.get(`/propostas/${id}`).then(r => r.data.data),
  });

  const updateMut = useMutation({
    mutationFn: (data) => api.put(`/propostas/${id}`, data),
    onSuccess: (res) => {
      qc.invalidateQueries(['proposta', id]);
      qc.invalidateQueries(['propostas']);
      qc.invalidateQueries(['unidades']);
      setEditing(false);
      if (res.data.unidade_liberada) {
        setUnidadeLiberadaToast(true);
        setTimeout(() => setUnidadeLiberadaToast(false), 5000);
      }
    },
  });

  if (isLoading) return <div className="p-8 text-center text-gray-400">Carregando...</div>;
  const p = proposta;
  if (!p) return <div className="p-8 text-center text-gray-400">Proposta não encontrada</div>;

  const startEdit = () => { setForm({ ...p }); setEditing(true); };
  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  // anexos = arquivos livres da "Nova proposta" do Portal do Corretor
  const docs = [...(p.docs_pessoais || []), ...(p.docs_endereco || []), ...(p.docs_estado_civil || []), ...(p.anexos || [])];
  const brl = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  if (editing) return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setEditing(false)} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
        <h2 className="text-lg font-bold">Editando Proposta</h2>
      </div>
      <div className="bg-white rounded-xl shadow p-6 space-y-6">
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label>
          <select className="input-field" value={form.status || ''} onChange={set('status')}>
            <option value="pendente">Pendente</option>
            <option value="em_analise">Em Análise</option>
            <option value="aprovada">Aprovada</option>
            <option value="recusada">Recusada</option>
          </select>
        </div>
        <hr />
        <h3 className="font-bold text-sm">Corretor</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome</label><input className="input-field" value={form.corretor_nome || ''} onChange={set('corretor_nome')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CPF</label><input className="input-field" value={form.corretor_cpf || ''} onChange={set('corretor_cpf')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CRECI</label><input className="input-field" value={form.corretor_creci || ''} onChange={set('corretor_creci')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label><input className="input-field" value={form.corretor_email || ''} onChange={set('corretor_email')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp</label><input className="input-field" value={form.corretor_whatsapp || ''} onChange={set('corretor_whatsapp')} /></div>
        </div>
        <hr />
        <h3 className="font-bold text-sm">Cliente</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome</label><input className="input-field" value={form.cliente_nome || ''} onChange={set('cliente_nome')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CPF</label><input className="input-field" value={form.cliente_cpf || ''} onChange={set('cliente_cpf')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Endereço</label><input className="input-field" value={form.cliente_endereco || ''} onChange={set('cliente_endereco')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Estado Civil</label><input className="input-field" value={form.cliente_estado_civil || ''} onChange={set('cliente_estado_civil')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email</label><input className="input-field" value={form.cliente_email || ''} onChange={set('cliente_email')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">WhatsApp</label><input className="input-field" value={form.cliente_whatsapp || ''} onChange={set('cliente_whatsapp')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Profissão</label><input className="input-field" value={form.cliente_profissao || ''} onChange={set('cliente_profissao')} /></div>
        </div>
        <hr />
        <h3 className="font-bold text-sm">Cônjuge</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nome</label><input className="input-field" value={form.conjuge_nome || ''} onChange={set('conjuge_nome')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">CPF</label><input className="input-field" value={form.conjuge_cpf || ''} onChange={set('conjuge_cpf')} /></div>
          <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Profissão</label><input className="input-field" value={form.conjuge_profissao || ''} onChange={set('conjuge_profissao')} /></div>
        </div>
        <hr />
        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Observações</label><textarea className="input-field" rows={3} value={form.observacoes || ''} onChange={set('observacoes')} /></div>
        <div className="flex gap-3">
          <button onClick={() => updateMut.mutate(form)} className="btn-primary flex items-center gap-2"><Save size={16} /> Salvar</button>
          <button onClick={() => setEditing(false)} className="btn-secondary">Cancelar</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <button onClick={onBack} className="text-gray-500 hover:text-gray-700"><ArrowLeft size={20} /></button>
        <h2 className="text-lg font-bold">{p.empreendimento} — Unidade {p.unidade_numero}</h2>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-800'}`}>{p.status}</span>
        <div className="ml-auto flex gap-2">
          {p.status === 'aprovada' && (
            <button
              onClick={() => setShowGerarModal(true)}
              className="btn-primary flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              <FilePlus size={16} /> Gerar Contrato
            </button>
          )}
          <button onClick={startEdit} className="btn-secondary flex items-center gap-2"><Edit3 size={16} /> Editar</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-bold text-sm text-gray-500 uppercase mb-4">Corretor</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Nome</span><span className="font-medium">{p.corretor_nome}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">CPF</span><span>{p.corretor_cpf}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">CRECI</span><span>{p.corretor_creci}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Email</span><span>{p.corretor_email}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">WhatsApp</span><span>{p.corretor_whatsapp}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Imobiliária</span><span>{p.corretor_imobiliaria_nome}</span></div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-bold text-sm text-gray-500 uppercase mb-4">Cliente</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Nome</span><span className="font-medium">{p.cliente_nome}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">CPF</span><span>{p.cliente_cpf}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Endereço</span><span>{p.cliente_endereco}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Estado Civil</span><span>{p.cliente_estado_civil}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Email</span><span>{p.cliente_email}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">WhatsApp</span><span>{p.cliente_whatsapp}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Profissão</span><span>{p.cliente_profissao}</span></div>
          </div>
        </div>

        {p.conjuge_nome && (
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-bold text-sm text-gray-500 uppercase mb-4">Cônjuge</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Nome</span><span className="font-medium">{p.conjuge_nome}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">CPF</span><span>{p.conjuge_cpf}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Profissão</span><span>{p.conjuge_profissao}</span></div>
            </div>
          </div>
        )}

        {(p.valor_proposto != null || p.condicoes_pagamento) && (
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-bold text-sm text-gray-500 uppercase mb-4">Proposta do corretor</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Valor de tabela</span><span>{p.valor_tabela != null ? brl(p.valor_tabela) : '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Valor proposto</span>
                <span className="font-bold">{p.valor_proposto != null ? brl(p.valor_proposto) : '—'}
                  {p.valor_proposto != null && p.valor_tabela ? <span className="font-normal text-gray-500"> ({((p.valor_proposto / p.valor_tabela - 1) * 100).toFixed(1).replace('.', ',')}%)</span> : null}</span></div>
              {p.condicoes_pagamento && (
                <div><span className="text-gray-500 block mb-1">Condições de pagamento</span><p className="whitespace-pre-wrap">{p.condicoes_pagamento}</p></div>
              )}
            </div>
          </div>
        )}

        {p.simulacao_json && (
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-bold text-sm text-gray-500 uppercase mb-4">Simulação Financeira</h3>
            <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-auto max-h-48">{JSON.stringify(p.simulacao_json, null, 2)}</pre>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow p-6 mt-6">
        <h3 className="font-bold text-sm text-gray-500 uppercase mb-4">Documentos ({docs.length})</h3>
        {docs.length === 0 ? (
          <p className="text-gray-400 text-sm">Nenhum documento anexado.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {docs.map((d, i) => (
              <a key={i} href={`${api.defaults.baseURL}/propostas/${p.id}/docs/${d.path}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg text-sm hover:bg-blue-100 transition">
                <Download size={14} /> {d.name}
              </a>
            ))}
          </div>
        )}
      </div>

      {showGerarModal && (
        <GerarContratoModal
          proposta={p}
          onClose={() => setShowGerarModal(false)}
          onSuccess={() => {
            qc.invalidateQueries(['contratos']);
            setTimeout(() => navigate('/vendas/contratos'), 1800);
          }}
        />
      )}

      {unidadeLiberadaToast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-gray-900 text-white px-4 py-3 rounded-xl shadow-lg text-sm">
          <Unlock size={16} className="text-green-400 shrink-0" />
          Unidade {p.unidade_numero} liberada automaticamente — voltou para "disponível".
        </div>
      )}
    </div>
  );
}

// ── Lista de propostas ────────────────────────────────────────────
export default function PropostasList() {
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('todas');

  const { data, isLoading } = useQuery({
    queryKey: ['propostas'],
    queryFn: () => api.get('/propostas').then(r => r.data.data),
    refetchInterval: 30000,
  });

  if (selectedId) return (
    <PropostaDetail
      id={selectedId}
      onBack={() => setSelectedId(null)}
    />
  );

  const propostas = data || [];
  const filtered = filter === 'todas' ? propostas : propostas.filter(p => p.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-800">Propostas</h1>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {['todas', 'pendente', 'em_analise', 'aprovada', 'recusada'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === s ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {s === 'todas' ? 'Todas' : s === 'em_analise' ? 'Em Análise' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-400 py-12">Nenhuma proposta encontrada.</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Data</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Corretor</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Cliente</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Unidade</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Empreendimento</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString('pt-BR')}</td>
                  <td className="px-4 py-3"><div className="font-medium">{p.corretor_nome}</div><div className="text-xs text-gray-400">{p.corretor_creci}</div></td>
                  <td className="px-4 py-3"><div className="font-medium">{p.cliente_nome}</div><div className="text-xs text-gray-400">{p.cliente_cpf}</div></td>
                  <td className="px-4 py-3 font-medium">{p.unidade_numero}{p.torre && <span className="text-gray-400 text-xs ml-1">T{p.torre}</span>}</td>
                  <td className="px-4 py-3 text-gray-600">{p.empreendimento}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-800'}`}>{p.status}</span></td>
                  <td className="px-4 py-3 flex items-center gap-2">
                    <button onClick={() => setSelectedId(p.id)} className="text-blue-600 hover:text-blue-800 font-medium text-xs flex items-center gap-1"><FileText size={14} /> Ver</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
